import { useCallback, useRef, useState } from 'react';
import useOAuthPopup, {
  UseOAuthPopupConfig,
} from '../useOAuthPopup/useOAuthPopup';
import { AuthInitiator, WithAuthentication } from './types';

type PickedPopupProps<TData> = Pick<
  UseOAuthPopupConfig<TData>,
  'onCancel' | 'postMessageOrigin'
>;

export interface UseOAuthenticatorConfig<TData>
  extends PickedPopupProps<TData> {
  /**
   * This function is called to kick off the authentication & authorization flows
   * when `authenticte()` is called.
   *
   * The data returned by `authInitiator()` will be passed back to the parent
   * when `onSuccess` is called as a result of successful authentication.
   */
  authInitiator: AuthInitiator<TData>;
  /**
   * This prop is only used for the `withAuthentication` wrapper returned by this
   * hook.
   *
   * This hook keeps track of successful authentication.  Once the user is
   * successfully authenticated, `withAuthentication` will not be send the user
   * through the auth flow again unless `force` is set to true.
   *
   * Auth status and whether or not to force is likely known by the parent component,
   * but having this prop makes it a little easier to deal with callbacks rather
   * than conditionally wrapping in `withAuthentication` only when necessary.
   */
  force?: boolean;
  /**
   * Called at the start of the authentication process, soon after `authenticate`
   * is called.
   *
   * `onAuthenticate` signals the beginning of an auth flow.
   */
  onAuthenticate?: () => void;
  /**
   * Called when the auth popup (or tab) closes for any reason
   */
  onClose?: () => void;
  /**
   * Called anytime there is an error, which is generally:
   * - when `authenticate()` throws an error
   * - when the backend returns an error during the auth flow
   * - if the browser blocks the auth popup/tab for any reason
   *
   * `onError` signals the end of an auth flow
   */
  onError?: (err: Error) => void;
  /**
   * Called if authentication succeeds
   *
   * `onSuccess` signals the end of an auth flow
   */
  onSuccess?: (data: TData) => void;
}

export interface UseOAuthenticatorResult {
  /**
   * Called to start authentication
   */
  authenticate: () => void;
  /**
   * True when authentication is in progress, false otherwise.  Gets set to true
   * when `onAuthenticate` is called and false when `onError` or `onSuccess`
   * is called
   */
  authenticating: boolean;
  /**
   * Takes a function and wraps it in an authentication guard.
   *
   * This is useful for handling user gestures that sometimes trigger auth and
   * other times trigger some other action.
   *
   * For example, when posting to a social platform, there might be a button that
   * allows you to post if you're logged in, otherwise sends you through the auth
   * flow before allowing you to post.  This prop can wrap the event handler on the
   * button that triggers the post action so that the user is sent through the auth
   * flow when necessary.
   *
   * For example:
   * ```
   *  <button onClick={withAuthentication(openPostModal}>post</button>
   * ```
   */
  withAuthentication: WithAuthentication;
}

/**
 * Basic hook to handle OAuth flow.
 *
 * The flow roughly works as follows:
 * 1. parent calls `authenticate`
 * 2.  a new tab is opened as a placeholder to send user to 3rd party site if auth is necessary
 * 3. run `authInitiator`.  if user is authenticated, close popup and call `onSuccess`, otherwise...
 * 4. use requestUrl from `authInitiator` result to load tab that was opened earlier
 * 5. user completes authentication flow in separate tab
 * 6. 3rd party OAuth flow calls an endpoint on our end with auth result
 * 7. Our endpoint renders a blank HTML page that communicates to this library via postMessage
 * 8. Auth data is passed to our applications via postMessage and tab is closed
 * 9. Auth data is passed from our app to our backend to authenticate the user with our product
 */
export default function useOAuthenticator<TData>({
  authInitiator,
  force = false,
  onAuthenticate,
  onCancel,
  onClose,
  onError,
  onSuccess,
  postMessageOrigin,
}: UseOAuthenticatorConfig<TData>): UseOAuthenticatorResult {
  const [authenticating, setAuthenticating] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const withAuthCallbackRef = useRef<(() => void) | null>(null);

  const handleClose = useCallback(() => {
    setAuthenticating(false);
    onClose?.();
    withAuthCallbackRef.current = null;
  }, [onClose]);

  const handleError = useCallback(
    (err: Error) => {
      setAuthenticating(false);
      setAuthenticated(false);
      onError?.(err);
      withAuthCallbackRef.current = null;
    },
    [onError],
  );

  const handleSuccess: UseOAuthPopupConfig<TData>['onSuccess'] = useCallback(
    (data) => {
      setAuthenticating(false);
      setAuthenticated(true);
      onSuccess?.(data);
      withAuthCallbackRef.current?.();
      withAuthCallbackRef.current = null;
    },
    [onSuccess],
  );

  const {
    close: closePopup,
    open: openPopup,
    setPopupUrl,
  } = useOAuthPopup({
    onCancel,
    postMessageOrigin,
    onClose: handleClose,
    onError: handleError,
    onSuccess: handleSuccess,
  });

  const authenticate = useCallback(async () => {
    setAuthenticating(true);
    onAuthenticate?.();

    // popup needs to open ASAP in the event handler so that it doesn't get
    // blocked by browser popup blockers
    openPopup();

    try {
      const initiatorResult = await authInitiator();

      if (initiatorResult.type === 'unauthenticated') {
        const { requestUrl } = initiatorResult;
        // this is the handoff to the 3rd parth oauth flow.  control returns
        // back to this hook when the popup is closed (`handleClose`), auth
        // succeeds (`handleSuccess`) or auth fails (`handleError`).
        setPopupUrl(requestUrl);
      } else {
        // authInitiator might return the auth data itself, indicating that the
        // user is already authenticated.
        const { data } = initiatorResult;
        handleSuccess(data);
        closePopup();
      }
    } catch (e) {
      const error = new Error('Error authenticating');
      closePopup();
      handleError(error);
    }
  }, [
    authInitiator,
    closePopup,
    handleError,
    handleSuccess,
    onAuthenticate,
    openPopup,
    setPopupUrl,
  ]);

  const withAuthentication: WithAuthentication = useCallback(
    (fn, shouldForce = force) => {
      if (authenticated && !shouldForce) {
        return fn;
      }

      return (...args) => {
        withAuthCallbackRef.current = () => fn(...args);
        authenticate();
      };
    },
    [authenticate, authenticated, force],
  );

  return {
    authenticate,
    authenticating,
    withAuthentication,
  };
}
